import { Injectable, inject, signal } from '@angular/core';
import {
  CreateInternalNoteGQL,
  DeleteInternalNoteGQL,
  GetInternalFilesForVehiclesGQL,
  GetInternalNotesForVehiclesGQL,
  InternalNoteType,
  UnlinkInternalFileGQL,
  UpdateInternalNoteGQL,
  type InternalNoteColumnsFragment
} from '@graphql/generated';
import { Observable, forkJoin, map, of, switchMap, tap } from 'rxjs';
import type { OrderGuideAttachment } from './dashboard.types';
import {
  internalNoteToOrderGuideNote,
  sortOrderGuideNotes,
  type OrderGuideNote
} from './order-guide-note.util';

/**
 * API-backed Order Guide notes and attachments for dashboard tiles, keyed by vehicle. Notes live in
 * cmt."Note" with Type = Dashboard; attachments live in cmt."File". This store batch-loads them for
 * favorites and persists note add/remove/update and attachment unlink.
 */
@Injectable({ providedIn: 'root' })
export class OrderGuideNotesStore {
  private readonly notesQuery = inject(GetInternalNotesForVehiclesGQL);
  private readonly filesQuery = inject(GetInternalFilesForVehiclesGQL);
  private readonly createNote = inject(CreateInternalNoteGQL);
  private readonly updateNote = inject(UpdateInternalNoteGQL);
  private readonly deleteNote = inject(DeleteInternalNoteGQL);
  private readonly unlinkFile = inject(UnlinkInternalFileGQL);

  private readonly notesByVehicleId = signal<ReadonlyMap<number, readonly OrderGuideNote[]>>(new Map());
  private readonly filesByVehicleId = signal<ReadonlyMap<number, readonly OrderGuideAttachment[]>>(
    new Map()
  );

  /** Reactive maps for computed dashboard tiles. */
  readonly notesMap = this.notesByVehicleId.asReadonly();
  readonly filesMap = this.filesByVehicleId.asReadonly();

  notesFor(vehicleId: number): OrderGuideNote[] {
    return [...(this.notesByVehicleId().get(vehicleId) ?? [])];
  }

  filesFor(vehicleId: number): OrderGuideAttachment[] {
    return [...(this.filesByVehicleId().get(vehicleId) ?? [])];
  }

  /** Batch-load notes and attachments for the given vehicles (call after favorites load). */
  loadForVehicles(vehicleIds: readonly number[]): void {
    const ids = [...new Set(vehicleIds)];
    if (ids.length === 0) {
      this.notesByVehicleId.set(new Map());
      this.filesByVehicleId.set(new Map());
      return;
    }

    this.notesQuery
      .fetch({
        variables: { vehicleIds: ids, noteType: InternalNoteType.Dashboard },
        fetchPolicy: 'network-only'
      })
      .subscribe((result) => {
        this.notesByVehicleId.set(this.groupNotes(result.data?.internalNotesForVehicles ?? []));
      });

    this.filesQuery
      .fetch({ variables: { vehicleIds: ids }, fetchPolicy: 'network-only' })
      .subscribe((result) => {
        const files = result.data?.internalFilesForVehicles ?? [];
        this.filesByVehicleId.set(this.groupFiles(files));
      });
  }

  /**
   * Persist the final note list for a vehicle: create notes staged with a negative id, update
   * persisted notes whose text changed, delete notes removed in the dialog, then refresh.
   */
  saveNotes(vehicleId: number, finalNotes: readonly OrderGuideNote[]): Observable<void> {
    const existing = this.notesFor(vehicleId);
    const existingById = new Map(existing.map((n) => [n.id, n]));
    const savedIds = new Set(finalNotes.filter((n) => n.id > 0).map((n) => n.id));
    const toDelete = existing.filter((n) => !savedIds.has(n.id));
    const toCreate = finalNotes.filter((n) => n.id < 0);
    const toUpdate = finalNotes.filter((n) => {
      if (n.id <= 0) {
        return false;
      }
      const prior = existingById.get(n.id);
      return prior != null && prior.text !== n.text;
    });

    const ops: Observable<unknown>[] = [
      ...toDelete.map((n) => this.deleteNote.mutate({ variables: { input: { noteId: n.id } } })),
      ...toCreate.map((n) =>
        this.createNote.mutate({
          variables: { input: { vehicleId, noteText: n.text, noteType: InternalNoteType.Dashboard } }
        })
      ),
      ...toUpdate.map((n) =>
        this.updateNote.mutate({ variables: { input: { noteId: n.id, noteText: n.text } } })
      )
    ];

    if (ops.length === 0) {
      return of(void 0);
    }

    return forkJoin(ops).pipe(switchMap(() => this.reloadNotesForVehicle(vehicleId)));
  }

  /** Remove an attachment link from a vehicle, then refresh that vehicle's attachments. */
  unlinkAttachment(vehicleId: number, fileId: number): Observable<void> {
    return this.unlinkFile
      .mutate({ variables: { input: { vehicleId, fileId } } })
      .pipe(switchMap(() => this.reloadFilesForVehicle(vehicleId)));
  }

  /** Refresh a single vehicle's attachments (e.g. after uploads happened over REST). */
  reloadFilesForVehicle(vehicleId: number): Observable<void> {
    return this.filesQuery
      .fetch({ variables: { vehicleIds: [vehicleId] }, fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data?.internalFilesForVehicles ?? []),
        tap((files) => {
          const next = new Map(this.filesByVehicleId());
          if (files.length === 0) {
            next.delete(vehicleId);
          } else {
            next.set(vehicleId, files);
          }
          this.filesByVehicleId.set(next);
        }),
        map(() => void 0)
      );
  }

  private reloadNotesForVehicle(vehicleId: number): Observable<void> {
    return this.notesQuery
      .fetch({
        variables: { vehicleIds: [vehicleId], noteType: InternalNoteType.Dashboard },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map((result) =>
          sortOrderGuideNotes(
            (result.data?.internalNotesForVehicles ?? []).map(internalNoteToOrderGuideNote)
          )
        ),
        tap((notes) => {
          const next = new Map(this.notesByVehicleId());
          if (notes.length === 0) {
            next.delete(vehicleId);
          } else {
            next.set(vehicleId, notes);
          }
          this.notesByVehicleId.set(next);
        }),
        map(() => void 0)
      );
  }

  private groupNotes(
    rawNotes: readonly InternalNoteColumnsFragment[]
  ): ReadonlyMap<number, readonly OrderGuideNote[]> {
    const byVehicle = new Map<number, OrderGuideNote[]>();
    for (const raw of rawNotes) {
      const note = internalNoteToOrderGuideNote(raw);
      const bucket = byVehicle.get(raw.vehicleId);
      if (bucket) {
        bucket.push(note);
      } else {
        byVehicle.set(raw.vehicleId, [note]);
      }
    }
    const result = new Map<number, readonly OrderGuideNote[]>();
    for (const [vehicleId, bucket] of byVehicle) {
      result.set(vehicleId, sortOrderGuideNotes(bucket));
    }
    return result;
  }

  private groupFiles(
    files: readonly OrderGuideAttachment[]
  ): ReadonlyMap<number, readonly OrderGuideAttachment[]> {
    const byVehicle = new Map<number, OrderGuideAttachment[]>();
    for (const file of files) {
      const bucket = byVehicle.get(file.vehicleId);
      if (bucket) {
        bucket.push(file);
      } else {
        byVehicle.set(file.vehicleId, [file]);
      }
    }
    return byVehicle;
  }
}
